const ROOT_ID = 'slot-landscape-blocker';

/**
 * Full-screen overlay for mobile phones in landscape — animated rotate-to-portrait prompt.
 * @param {{
 *   mountEl?: HTMLElement | null,
 *   isActive: () => boolean,
 *   getTitle: () => string,
 *   getBody: () => string,
 * }} opts
 */
export function createLandscapeOrientationBlocker({
  mountEl = typeof document !== 'undefined' ? document.body : null,
  isActive,
  getTitle,
  getBody,
} = {}) {
  if (!mountEl || typeof document === 'undefined') {
    return { sync: () => {}, dispose: () => {} };
  }

  let root = document.getElementById(ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = ROOT_ID;
    root.className = 'slot-landscape-blocker';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-live', 'polite');
    root.innerHTML = `
      <div class="slot-landscape-blocker__backdrop" aria-hidden="true"></div>
      <div class="slot-landscape-blocker__content">
        <div class="slot-landscape-blocker__visual" aria-hidden="true">
          <svg class="slot-landscape-blocker__orbit" viewBox="0 0 220 220" fill="none">
            <defs>
              <linearGradient id="slot-blocker-orbit-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#f6e27a" stop-opacity="0.15"/>
                <stop offset="45%" stop-color="#d4af37" stop-opacity="0.95"/>
                <stop offset="100%" stop-color="#8b6914" stop-opacity="0.35"/>
              </linearGradient>
            </defs>
            <circle cx="110" cy="110" r="92" stroke="url(#slot-blocker-orbit-grad)" stroke-width="2.5" stroke-dasharray="10 14" opacity="0.55"/>
            <path class="slot-landscape-blocker__orbit-arrow" d="M110 18 A92 92 0 0 1 198 110" stroke="url(#slot-blocker-orbit-grad)" stroke-width="4" stroke-linecap="round"/>
            <polygon class="slot-landscape-blocker__orbit-head" points="198,110 186,102 186,118" fill="#e8c547"/>
          </svg>
          <div class="slot-landscape-blocker__phone-wrap">
            <svg class="slot-landscape-blocker__phone" viewBox="0 0 120 200" fill="none">
              <defs>
                <linearGradient id="slot-blocker-phone-frame" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#fff4b8"/>
                  <stop offset="35%" stop-color="#d4af37"/>
                  <stop offset="100%" stop-color="#7a5a12"/>
                </linearGradient>
                <linearGradient id="slot-blocker-phone-screen" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stop-color="#163012"/>
                  <stop offset="55%" stop-color="#0a1808"/>
                  <stop offset="100%" stop-color="#061006"/>
                </linearGradient>
                <filter id="slot-blocker-phone-glow" x="-40%" y="-40%" width="180%" height="180%">
                  <feDropShadow dx="0" dy="0" stdDeviation="8" flood-color="#d4af37" flood-opacity="0.45"/>
                </filter>
              </defs>
              <rect x="6" y="6" width="108" height="188" rx="22" fill="#120f08" stroke="url(#slot-blocker-phone-frame)" stroke-width="3" filter="url(#slot-blocker-phone-glow)"/>
              <rect x="14" y="24" width="92" height="152" rx="10" fill="url(#slot-blocker-phone-screen)"/>
              <rect x="40" y="12" width="40" height="7" rx="3.5" fill="#274622" opacity="0.9"/>
              <rect x="44" y="182" width="32" height="4" rx="2" fill="#3d6b32" opacity="0.55"/>
              <circle cx="60" cy="188" r="2.5" fill="#5a8f4a" opacity="0.35"/>
              <g class="slot-landscape-blocker__phone-icons" opacity="0.55">
                <rect x="28" y="44" width="18" height="18" rx="4" fill="#c9a227" opacity="0.35"/>
                <rect x="52" y="44" width="18" height="18" rx="4" fill="#c9a227" opacity="0.25"/>
                <rect x="76" y="44" width="18" height="18" rx="4" fill="#c9a227" opacity="0.35"/>
                <rect x="28" y="68" width="18" height="18" rx="4" fill="#c9a227" opacity="0.25"/>
                <rect x="52" y="68" width="18" height="18" rx="4" fill="#c9a227" opacity="0.45"/>
                <rect x="76" y="68" width="18" height="18" rx="4" fill="#c9a227" opacity="0.25"/>
              </g>
            </svg>
          </div>
        </div>
        <h1 class="slot-landscape-blocker__title" tabindex="-1"></h1>
        <p class="slot-landscape-blocker__body"></p>
      </div>
    `;
    mountEl.appendChild(root);
  }

  const titleEl = root.querySelector('.slot-landscape-blocker__title');
  const bodyEl = root.querySelector('.slot-landscape-blocker__body');

  const refreshCopy = () => {
    if (titleEl) titleEl.textContent = getTitle?.() ?? '';
    if (bodyEl) bodyEl.textContent = getBody?.() ?? '';
    const label = [getTitle?.(), getBody?.()].filter(Boolean).join('. ');
    if (label) root.setAttribute('aria-label', label);
  };

  const sync = () => {
    refreshCopy();
    const show = !!isActive?.();
    root.classList.toggle('slot-landscape-blocker--visible', show);
    root.toggleAttribute('inert', !show);
    if (show) {
      titleEl?.focus?.({ preventScroll: true });
    }
  };

  refreshCopy();

  return {
    sync,
    refreshCopy,
    dispose() {
      root?.remove();
    },
  };
}
